import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'reactstrap';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';

/**
 * CollapsibleSection
 *
 * A reusable open/close (collapsible) section for grouping form fields.
 * Renders a clickable header with a chevron that toggles the visibility of its
 * children. Generic on purpose so it can wrap any section of the budget form
 * (Keywords today, more sections later).
 *
 * @param {string} title - Header label for the section
 * @param {boolean} defaultOpen - Whether the section starts expanded (default: false)
 * @param {node} headerExtra - Optional content rendered beside the title (e.g. a toggle)
 * @param {node} children - The content rendered inside the collapsible body
 */
const CollapsibleSection = ({ title, defaultOpen, headerExtra, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // When defaultOpen becomes true after mount (e.g. the form reinitializes with
  // existing data on edit), auto-expand the section. We only force it open, so
  // a user manually collapsing it afterwards is still respected.
  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  return (
    <div className="form__form-group" style={{ width: '100%', marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          aria-expanded={isOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            width: headerExtra ? 'auto' : '100%',
            border: 'none',
            background: 'transparent',
            padding: '8px 0',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'left',
            color: 'inherit',
          }}
        >
          {isOpen ? <ChevronDownIcon size={18} /> : <ChevronRightIcon size={18} />}
          <span className="form__form-group-label" style={{ margin: 0 }}>{title}</span>
        </button>
        {headerExtra}
      </div>
      <Collapse isOpen={isOpen} style={{ width: '100%' }}>
        <div style={{ width: '100%', paddingTop: '8px' }}>
          {children}
        </div>
      </Collapse>
    </div>
  );
};

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  headerExtra: PropTypes.node,
  children: PropTypes.node.isRequired,
};

CollapsibleSection.defaultProps = {
  defaultOpen: false,
  headerExtra: null,
};

export default CollapsibleSection;
